// featureProgress.mjs — /feature-progress 的纯逻辑（SB-201 S3，第三百零八批；需求方指令「类似资产对账的 tab」）。
//
// 与 assetProgress.mjs 同一条纪律（SB-042：可判定逻辑入 .mjs 配单测），判断集中在这里：
//   · 「这一格谁能记」——角色矩阵是 feature-events.mjs ALLOWED_ROLES 的第三份抄本，单测逐格钉住；
//   · 「值该从哪个枚举取」——三态各自的值集（implemented: not_started|partial|done；self_tested／qa_passed:
//     none|passed），选错枚举的症状是 400，而 400 之前页面就该拦住；
//   · 「证据什么时候必填」——只在 done／passed（承认没做完不需要证据，主张做完了才需要）；证据建议锚定
//     git 提交（需求方 2026-09-24 指令）——页面给提示、不硬拦（工单号／PR 也是可查的证据）；
//   · 「supersedes 该填什么」——由 latest 决定，不由人猜；409 之后刷新再提。
// 语言随界面：本文件只出 i18n 键，不落任何文案。

export const STATES = ['implemented', 'self_tested', 'qa_passed'];
export const VALUES = { implemented: ['not_started', 'partial', 'done'], self_tested: ['none', 'passed'], qa_passed: ['none', 'passed'] };
export const ALL_VALUES = ['not_started', 'partial', 'done', 'none', 'passed'];

// 角色矩阵（需求方 2026-09-23 裁）：程序记实现与自测；质量记 QA；owner／admin／workspace 三态皆可。
export function recordableStates(role) {
  if (role === 'program') return ['implemented', 'self_tested'];
  if (role === 'qa') return ['qa_passed'];
  if (role === 'owner' || role === 'admin' || role === 'workspace') return [...STATES];
  return [];
}
export function mayRecord(role, state) {
  return recordableStates(role).includes(state);
}

// latest：服务端给的 FP → 态 → 最新事件。取不到一律 null，不走原型链。
export function latestFor(latest, fp, state) {
  const byFp = latest && Object.prototype.hasOwnProperty.call(latest, fp) ? latest[fp] : null;
  if (!byFp || typeof byFp !== 'object') return null;
  return Object.prototype.hasOwnProperty.call(byFp, state) && byFp[state] ? byFp[state] : null;
}

// 版本序：vX.Y 按数值；别的形排最后按文本。
export function versionKey(v) {
  const m = /^v(\d+)\.(\d+)$/.exec(String(v || ''));
  return m ? [Number(m[1]), Number(m[2])] : [1e9, 0];
}
function cmpVersion(a, b) {
  const [a1, a2] = versionKey(a), [b1, b2] = versionKey(b);
  return a1 - b1 || a2 - b2 || (a < b ? -1 : a > b ? 1 : 0);
}

// ── 第三百一十三批（S4，需求方 2026-09-25 指令）：两级 ────────────────────────────
// 子 fp 形 `FP-####/<条目 id>`；父自己也是节点、不由子派生；父子平等计入分母。
export const isChildFp = (fp) => /\//.test(String(fp));
export const parentFpOf = (fp) => String(fp).split('/')[0];

// 巢状树：启用版本 → 父行（按 FP 号）→ 子行（按出现序）。与功能表 §3 同形；同一份输入永远同一棵树。
// 每个父条目带 `children`；版本的 count／progress 数的是**全部节点**（父＋子）。
export function buildTree(rows, latest) {
  const byVersion = new Map();
  const entryOf = (r) => ({ fp: r.fp, row: r, cells: Object.fromEntries(STATES.map((s) => [s, latestFor(latest, r.fp, s)])), children: [] });
  const parents = new Map();
  const orphans = [];
  for (const r of rows || []) {
    if (isChildFp(r.fp)) { orphans.push(r); continue; }
    const v = String(r.version || '—');
    if (!byVersion.has(v)) byVersion.set(v, []);
    const e = entryOf(r);
    byVersion.get(v).push(e);
    parents.set(r.fp, e);
  }
  for (const r of orphans) {
    const p = parents.get(parentFpOf(r.fp));
    if (p) { p.children.push(entryOf(r)); continue; }
    // 父不在镜像：按自己的版本挂成一个独立节点（不丢行，但会在页上看出来）
    const v = String(r.version || '—');
    if (!byVersion.has(v)) byVersion.set(v, []);
    byVersion.get(v).push(entryOf(r));
  }
  const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
  return [...byVersion.entries()].sort((a, b) => cmpVersion(a[0], b[0])).map(([version, list]) => {
    const sorted = list.sort((a, b) => cmp(a.fp, b.fp));
    const nodes = sorted.flatMap((e) => [e, ...e.children]);
    return { version, rows: sorted, count: nodes.length, parents: sorted.length, children: nodes.length - sorted.length, progress: progressOf(nodes) };
  });
}

// 进度：实现＝done 的节点数、自测／QA＝passed 的节点数；分母＝节点数（父＋子；零行显 0／0）。
export function progressOf(entries) {
  const list = entries || [];
  return {
    implemented: list.filter((e) => e.cells && e.cells.implemented && e.cells.implemented.value === 'done').length,
    self_tested: list.filter((e) => e.cells && e.cells.self_tested && e.cells.self_tested.value === 'passed').length,
    qa_passed: list.filter((e) => e.cells && e.cells.qa_passed && e.cells.qa_passed.value === 'passed').length,
    total: list.length,
  };
}

// 证据什么时候必填：done／passed。
export function evidenceRequired(value) {
  return value === 'done' || value === 'passed';
}

// 证据像不像 git 提交（7～40 位十六进制，或含 "sha"／"commit"／PR 号）——只是提示位，不拦。
export function looksLikeCommitEvidence(evidence) {
  const s = String(evidence ?? '').trim();
  return /\b[0-9a-f]{7,40}\b/i.test(s) || /\b(sha|commit|pr\s*#?\d+|pull)\b/i.test(s);
}

// 记态请求体。value 须在该态的枚举里；done／passed 证据必填；supersedes 取该格最新 id。
export function submitPayload({ group, fp, state, value, evidence, note, latest }) {
  const ev = String(evidence ?? '').trim();
  if (!fp || !STATES.includes(state)) return { ok: false, errorKey: 'featureProgress.err.rule' };
  if (!VALUES[state].includes(value)) return { ok: false, errorKey: 'featureProgress.err.rule' };
  if (evidenceRequired(value) && !ev) return { ok: false, errorKey: 'featureProgress.evidenceRequired' };
  const cur = latestFor(latest, fp, state);
  return {
    ok: true,
    body: { group: group || 'MirrorMii', fp, state, value, evidence: ev, note: String(note ?? '').trim(), supersedes: cur ? cur.id : null },
  };
}

// 值 → MUI Chip 颜色。done／passed 绿、partial 黄、其余中性。
export function valueColor(value) {
  if (value === 'done' || value === 'passed') return 'success';
  if (value === 'partial') return 'warning';
  return 'default';
}

// 某 FP 的全部事件，新在前。
export function historyOf(events, fp) {
  return (events || []).filter((e) => e.fp === fp).sort((a, b) => b.id - a.id);
}

// 错误 → i18n 键。
export function errorKeyFor(e) {
  const status = e && typeof e.status === 'number' ? e.status : 0;
  const code = e && e.code ? String(e.code) : '';
  if (status === 409 || code === 'SUPERSEDES_STALE') return 'featureProgress.err.stale';
  if (status === 403) return 'featureProgress.err.forbidden';
  if (status === 400) return 'featureProgress.err.rule';
  if (status === 404) return 'featureProgress.err.notFound';
  return 'featureProgress.err.failed';
}

// 查询串里的组（?group=）；不合形状退回缺省。
export function groupFromSearch(search, dflt = 'MirrorMii') {
  const m = /[?&]group=([A-Za-z0-9-]+)/.exec(String(search || ''));
  return m ? m[1] : dflt;
}

// 规格出处格带 md 链接（`FR-U1～U3（[v1.8 册 01 §1](../../…)）`）——页面只要能读的字。
export function plainText(s) {
  return String(s ?? '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
}
